import {
  type CamInsightsRecordingFetcherInput,
  fetchCamInsightsRecording,
} from 'common/api/cameras';
import { SL_WEB_CAM_PAYWALL_REDESIGN } from 'common/treatments';
import { RewindVideo } from 'components/RewindCamPlayer/plugins/rewindStitcher/src/rewindStitcher.types';
import config from 'config';
import { roundToNearestMinutes } from 'date-fns';
import useCurrentTimestamp, { CurrentTimestamp } from 'hooks/useCurrentTimestamp';
import useNonPremiumRewindTime from 'hooks/useNonPremiumRewindTime';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUserPermissionStatus } from 'selectors/user';
import useSWR from 'swr';
import { Camera } from 'types/camera';
import { RewindRecording } from 'types/recordings';
import { generateSmartRewindPlaylistBlocks } from 'utils/rewinds/generateSmartRewindPlaylistBlocks';
import {
  firstPreviouslyCompletePlaylist,
  calculateMatchingPlaylistBlock,
} from 'utils/rewinds/playlistBlocks';
import { RewindPlaylists, SmartRewindPlaylistBlock } from 'utils/rewinds/playlistBlocks.types';
import { ceilToMidnightForTZ, floorToMidnightForTZ, oneDay, oneMinute } from 'utils/time';
import { useTreatments } from 'utils/treatments';

const DEFAULT_DAYS_TO_LOAD = config.numberOfRewindDays;
const PLAYLIST_BLOCK_LENGTH = 30; // 30 minutes
const REWIND_PLAYLIST_BLOCK_OVERLAP = 0;

export const REWIND_PLAYLIST_BLOCK_LENGTH_IN_MS = PLAYLIST_BLOCK_LENGTH * oneMinute;
export const REWIND_PLAYLIST_BLOCK_OVERLAP_IN_MS = REWIND_PLAYLIST_BLOCK_OVERLAP * oneMinute;

export const RewindsContext = createContext({} as RewindsContextProps);

interface RewindsContextProviderProps {
  children: React.ReactNode;
  surfHeightUnit: string | undefined;
  selectedCamera: Camera;
  timezone: string;
}

export interface RewindsContextProps {
  rewindsPlaylists: RewindPlaylists | undefined;
  currentPlaylist: SmartRewindPlaylistBlock | undefined;
  updateCurrentPlaylist: (newPlaylistTimestamp: string) => void;
  error: boolean;
  reFetch: () => void;
  rewindsLoading: boolean;
  isCurrentPlaylistLatest: boolean;
  previouslyCompletedPlaylists: {
    morning?: SmartRewindPlaylistBlock;
    night?: SmartRewindPlaylistBlock;
  };
  currentTimestamp: CurrentTimestamp;
  isNonPremiumPlaylist: boolean;
  rewindList: RewindVideo[];
  nextAvailableRewindFromLive: string | undefined;
  bucketStartTimeInMs: number | null;
  bucketEndTimeInMs: number | null;
}

export const RewindsContextProvider = ({
  children,
  surfHeightUnit,
  selectedCamera,
  timezone,
}: RewindsContextProviderProps) => {
  const [rewindsPlaylists, setRewindsPlaylists] = useState<RewindPlaylists | undefined>(undefined);
  const [currentPlaylist, setCurrentPlaylist] = useState<SmartRewindPlaylistBlock | undefined>(
    undefined,
  );
  const now = useMemo(() => new Date().getTime(), []);
  const currentTimestamp = useCurrentTimestamp();
  const { currentTimestampRef, setCurrentTimestamp } = currentTimestamp;
  const initialisedWithTimestamp = useRef(!!currentTimestampRef.current.value);
  // Rounded to next 5 mins so we hit Edge Cache
  const endDate = useRef<number>(now);
  const roundedEnd = roundToNearestMinutes(endDate.current, { nearestTo: 5 }).getTime();

  const startDate = endDate.current - DEFAULT_DAYS_TO_LOAD * oneDay;
  const roundedStart = ceilToMidnightForTZ(startDate, timezone);

  const [responseData, setData] = useState<RewindRecording[] | undefined>(undefined);

  const treatments = useTreatments();
  const { hasAdFreeCamPermissions, hasAdSupportedCamPermissions } = useUserPermissionStatus();
  const paywallRedesignActive =
    !hasAdFreeCamPermissions &&
    !hasAdSupportedCamPermissions &&
    treatments?.[SL_WEB_CAM_PAYWALL_REDESIGN] === 'on';

  const nonPremiumRewindTime = useNonPremiumRewindTime(now, hasAdFreeCamPermissions);
  const [nonPremiumPlaylistKey, setNonPremiumPlaylistKey] = useState<string | undefined>();
  const [currentPlaylistKey, setCurrentPlaylistKey] = useState<string | undefined>();

  const { data, error, mutate, isLoading } = useSWR(
    {
      camId: selectedCamera._id,
      startDate: roundedStart,
      endDate: roundedEnd,
      surfHeightUnit,
    } as CamInsightsRecordingFetcherInput,
    fetchCamInsightsRecording,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const [rewindsLoading, setRewindsLoading] = useState<boolean>(isLoading);

  useEffect(() => {
    if (isLoading) {
      setRewindsLoading(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (error) {
      setRewindsLoading(false);
    }
  }, [error]);

  useEffect(() => {
    if (data && !error) {
      const updatedData = data.map((recording) => ({
        ...recording,
        startDate: new Date(recording.startDate).toISOString(),
        endDate: new Date(recording.endDate).toISOString(),
      }));
      setData(updatedData);
    }

    if (error) {
      setData([]);
    }
  }, [data, error]);

  const updateCurrentPlaylist = useCallback(
    (endTimeOfPlaylist: string) => {
      setCurrentPlaylist(rewindsPlaylists?.get(endTimeOfPlaylist));
      setCurrentPlaylistKey(endTimeOfPlaylist);
      setRewindsLoading(false);
    },
    [rewindsPlaylists],
  );

  const isCurrentPlaylistLatest = useMemo(() => {
    if (!currentPlaylist) return false;

    const currentTime = new Date().getTime();
    const playlistEndTime = new Date(currentPlaylist.bucketEnd).getTime();

    return currentTime - playlistEndTime <= PLAYLIST_BLOCK_LENGTH * oneMinute;
  }, [currentPlaylist]);

  useEffect(() => {
    if (responseData) {
      const sortedRewindsPlaylists = generateSmartRewindPlaylistBlocks(
        responseData,
        REWIND_PLAYLIST_BLOCK_LENGTH_IN_MS,
        REWIND_PLAYLIST_BLOCK_OVERLAP_IN_MS,
      );
      setRewindsPlaylists(sortedRewindsPlaylists);
    }
  }, [responseData]);

  const previouslyCompletedPlaylists = useMemo(
    () => ({
      morning: firstPreviouslyCompletePlaylist(
        rewindsPlaylists,
        floorToMidnightForTZ(now, timezone),
      ),
      night: firstPreviouslyCompletePlaylist(rewindsPlaylists, now),
    }),
    [now, rewindsPlaylists, timezone],
  );

  const getPlaylistForTimestamp = useCallback(
    (timestamp: number | undefined, playlists: RewindPlaylists) => {
      if (playlists?.size) {
        const firstKey = playlists.keys().next().value;
        if (firstKey) {
          const endTimeOfPlaylists = new Date(firstKey).getTime();
          if (timestamp && timestamp < endTimeOfPlaylists) {
            const matchingPlaylists = calculateMatchingPlaylistBlock(
              new Date(firstKey).getTime(),
              timestamp,
              REWIND_PLAYLIST_BLOCK_LENGTH_IN_MS,
              REWIND_PLAYLIST_BLOCK_OVERLAP_IN_MS,
            );
            return matchingPlaylists[0];
          }
        }
      }
      return undefined;
    },
    [],
  );

  const getNonPremiumPlaylistKey = useCallback(() => {
    if (paywallRedesignActive && nonPremiumRewindTime.time && rewindsPlaylists) {
      const matchingPlaylistKey = getPlaylistForTimestamp(
        nonPremiumRewindTime.time,
        rewindsPlaylists,
      );
      const matchedPlaylist = matchingPlaylistKey && rewindsPlaylists.get(matchingPlaylistKey);
      if (matchingPlaylistKey && matchedPlaylist && matchedPlaylist.rewinds.length) {
        return matchingPlaylistKey;
      }
    }
    return undefined;
  }, [paywallRedesignActive, nonPremiumRewindTime, getPlaylistForTimestamp, rewindsPlaylists]);

  useEffect(() => {
    if (rewindsPlaylists && !(paywallRedesignActive && nonPremiumRewindTime.loading)) {
      // If playlists is empty, stop loading
      if (rewindsPlaylists.size === 0) {
        setRewindsLoading(false);
        return;
      }

      const currentNonPremiumPlaylistKey = getNonPremiumPlaylistKey();
      setNonPremiumPlaylistKey(currentNonPremiumPlaylistKey);

      if (
        currentTimestampRef.current.value &&
        (initialisedWithTimestamp.current || !currentNonPremiumPlaylistKey)
      ) {
        const matchingPlaylistKey = getPlaylistForTimestamp(
          currentTimestampRef.current.value.getTime(),
          rewindsPlaylists,
        );
        if (matchingPlaylistKey && rewindsPlaylists.get(matchingPlaylistKey)) {
          updateCurrentPlaylist(matchingPlaylistKey);
          return;
        }
      }

      if (nonPremiumRewindTime.time && currentNonPremiumPlaylistKey) {
        updateCurrentPlaylist(currentNonPremiumPlaylistKey);
        return;
      }

      const firstKey = rewindsPlaylists.keys().next().value;
      if (firstKey) {
        updateCurrentPlaylist(firstKey);
      }
    }
  }, [
    paywallRedesignActive,
    rewindsPlaylists,
    getPlaylistForTimestamp,
    updateCurrentPlaylist,
    setCurrentTimestamp,
    currentTimestampRef,
    nonPremiumRewindTime,
    getNonPremiumPlaylistKey,
  ]);

  const reFetch = useCallback(() => {
    setRewindsLoading(true);
    mutate();
  }, [mutate]);

  const rewindList = useMemo(
    () =>
      currentPlaylist?.rewinds.map((rewind) => ({
        url: rewind.recordingUrl,
        startDateTime: rewind.startDate ? new Date(rewind.startDate) : new Date(),
        endDateTime: rewind.endDate ? new Date(rewind.endDate) : new Date(),
      })) || [],
    [currentPlaylist],
  );

  const nextAvailableRewindFromLive = useMemo(() => {
    const latestRewindsArray = rewindsPlaylists
      ? Array.from(rewindsPlaylists.values()).find((playlist) => playlist.rewinds.length > 0)
          ?.rewinds
      : undefined;
    return latestRewindsArray?.[latestRewindsArray.length - 1]?.startDate;
  }, [rewindsPlaylists]);

  const contextValue = useMemo(
    () => ({
      rewindsPlaylists,
      currentPlaylist,
      updateCurrentPlaylist,
      error,
      reFetch,
      rewindsLoading,
      isCurrentPlaylistLatest,
      previouslyCompletedPlaylists,
      currentTimestamp,
      isNonPremiumPlaylist: !!nonPremiumPlaylistKey && currentPlaylistKey === nonPremiumPlaylistKey,
      rewindList,
      nextAvailableRewindFromLive,
      bucketStartTimeInMs: currentPlaylist?.bucketStart?.getTime() ?? null,
      bucketEndTimeInMs: currentPlaylist?.bucketEnd?.getTime() ?? null,
    }),
    [
      rewindsLoading,
      currentPlaylist,
      error,
      isCurrentPlaylistLatest,
      reFetch,
      previouslyCompletedPlaylists,
      rewindsPlaylists,
      updateCurrentPlaylist,
      currentTimestamp,
      nonPremiumPlaylistKey,
      currentPlaylistKey,
      nextAvailableRewindFromLive,
      rewindList,
    ],
  );

  return <RewindsContext.Provider value={contextValue}>{children}</RewindsContext.Provider>;
};
